import { Component, Inject, OnInit, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material';
import { finalize, takeUntil, tap } from 'rxjs/operators';
import { BehaviorSubject, ReplaySubject } from 'rxjs';
import { DatePipe } from '@angular/common';
import { MatDialog } from '@angular/material/dialog';
import { Store } from '@ngrx/store';

import { ProductTypes } from '../../../products/product-types';
import { TableColumnModel } from '../../../../models/table-column.model';
import { SalesPosition, SalesPositionsByProductType } from 'projects/workspace/src/app/subscription/models/subscription-position.model';
import { SubscriptionApiService } from 'projects/workspace/src/app/subscription/services/subscription-api.service';
import { SubscriptionModel } from 'projects/workspace/src/app/subscription/models/subscription.model';
import { PositionGroupModel, ProductUnitModel, ResponseModel } from 'projects/workspace/src/app/shared/models';
import { UnitTypeTranslatePipe } from '../../../rnpl-common';
import {
  generatePositionsCardsInfo,
} from 'projects/workspace/src/app/subscription/pages/subscription-positions/subscription-positions.config';
import { UIStatesEnum } from '../../../../models';
import { ActionButtonsService } from '../../../../services/action-buttons.service';
import { CommonModalsActionsEnum, DangerModalComponent, PositionGroupModalComponent } from '../../modals-common';
import {
  ControlTypeEnum,
  InputDropdownTypeEnum,
  PositionInfoListModel,
  PositionItemTypeEnum
} from '../../../position-card/position-card-models.model';
import { PositionsActionsType } from '../../../ui-components/table/custom-table.enums';
import {
  selectSubscriptionCurrentState,
  selectSubscriptionPositions,
  selectSubscriptionPositionsGroups
} from 'projects/workspace/src/app/subscription/store/selectors';
import { AppState } from 'projects/workspace/src/app/store/state/app.state';
import { REMOVE_POSITION_MODAL_DATA } from '../../modals.contsans';
import { PositionGroupsService, ProductUnitApiService } from 'projects/workspace/src/app/shared/services';
import { PositionGroupingEnum, PositionMediaTypeEnum, PriceOptionEnum } from 'projects/workspace/src/app/shared/enums';
import { PositionMediaFormComponent } from 'projects/workspace/src/app/shared/forms/position-media-form/position-media-form.component';

@Component({
  selector: 'rnpl-subscription-edit-position-modal',
  templateUrl: './subscription-edit-position-modal.component.html',
  styleUrls: ['./subscription-edit-position-modal.component.scss']
})
export class SubscriptionEditPositionModalComponent implements OnInit {

  public positionsCardInfoByTypes: {[key in ProductTypes]?: any[]} = {
    [ProductTypes.SERVICES]: [] as TableColumnModel[],
    [ProductTypes.DIGITAL]: [] as TableColumnModel[]
  };

  public position: SalesPosition;
  public allPositions: SalesPosition[];
  public groupPositions: SalesPosition[];
  public positionsByGroup: PositionGroupModel[];
  public rowIndex: number;
  public positionItemTypeEnum: typeof PositionItemTypeEnum = PositionItemTypeEnum;
  public controlTypeEnum: typeof ControlTypeEnum = ControlTypeEnum;
  public inputDropdownTypeEnum = InputDropdownTypeEnum;
  public positionGroupingEnum = PositionGroupingEnum;
  public mediaTypeEnum = PositionMediaTypeEnum;

  public unitsList: string[] = [];
  public unitsListSource: ProductUnitModel[] = [];
  public isReadonly: boolean;
  public showControls: boolean = true;
  public positionsByProductType: {[key in ProductTypes]?: any[]};

  @ViewChild('positionMediaFormComponent', { static: false }) positionMediaFormComponentRef: PositionMediaFormComponent;

  public positionsUpdateRequest$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public readonly destroy$: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);

  constructor(
    public dialogRef: MatDialogRef<SubscriptionEditPositionModalComponent>,
    public subscriptionApi: SubscriptionApiService,
    private positionGroupsService: PositionGroupsService,
    private unitTypeTranslatePipe: UnitTypeTranslatePipe,
    private datePipe: DatePipe,
    public actionButtonsService: ActionButtonsService,
    private readonly dialog: MatDialog,
    private productUnitApiService: ProductUnitApiService,
    private readonly store: Store<AppState>,
    @Inject(MAT_DIALOG_DATA) public data: {
      currentSubscription: SubscriptionModel;
      position: SalesPosition,
      groups: PositionGroupModel[],
    }
  ) {
    this.dialogRef.keydownEvents().subscribe(event => {
      if (event.key === 'Escape') {
        this.dialogRef.close();
      }
    });
  }

  ngOnInit() {
    this.position = this.preparePosition(this.data.position);

    this.getProductUnits();
    this.getPositionsCards();
    this.trackSubscriptionStateChanges();
    this.trackPositionsChanges();
    this.trackPositionsGroupsChanges();
  }

  public getProductUnits(): void {
    this.productUnitApiService.getUnits$(this.position.productType)
      .pipe(takeUntil(this.destroy$))
      .subscribe((unitsList: ProductUnitModel[]) => {
        this.unitsListSource = unitsList;
        if (!this.position.general) {
          this.unitsList = [this.position.unitType];
          return;
        } else {
          this.unitsList = unitsList.map(u => u.name);
        }
        this.redrawControls();
      });
  }

  private trackSubscriptionStateChanges(): void {
    this.store.select(selectSubscriptionCurrentState)
      .pipe(takeUntil(this.destroy$))
      .subscribe((state: UIStatesEnum) => {
        this.isReadonly = state === UIStatesEnum.VIEW;
      });
  }

  private trackPositionsChanges(): void {
    if (this.data.currentSubscription.preferences.positionGrouping !== PositionGroupingEnum.PRODUCT_TYPE) { return; }
    this.positionsUpdateRequest$.next(true);
    this.store.select(selectSubscriptionPositions)
      .pipe(
        finalize(() => this.positionsUpdateRequest$.next(false)),
        takeUntil(this.destroy$)
      )
      .subscribe((positions: ResponseModel<SalesPositionsByProductType>) => {
        if (positions) {
          this.positionsByProductType = positions.data;
          this.setupPositionsAndGroups();
        }
      });
  }

  private trackPositionsGroupsChanges(): void {
    if (this.data.currentSubscription.preferences.positionGrouping !== PositionGroupingEnum.USER_DEFINED) { return; }
    this.positionsUpdateRequest$.next(true);
    this.store.select(selectSubscriptionPositionsGroups)
      .pipe(
        finalize(() => this.positionsUpdateRequest$.next(false)),
        takeUntil(this.destroy$)
      )
      .subscribe((positionsGroups: PositionGroupModel[]) => {
        this.positionsByGroup = positionsGroups;
        this.setupPositionsAndGroups();
      });
  }

  public setupPositionsAndGroups(): void {
    if (this.data.currentSubscription.preferences.positionGrouping === PositionGroupingEnum.PRODUCT_TYPE) {
      this.groupPositions = this.positionsByProductType[this.position.productType];
      this.allPositions = [].concat(Object.values(this.positionsByProductType));
    }
    if (this.data.currentSubscription.preferences.positionGrouping === PositionGroupingEnum.USER_DEFINED) {
      this.allPositions = [].concat(...this.positionsByGroup.map(item => item.positions));
      this.groupPositions = this.positionsByGroup
        .find(g => g.positions.some(p => p.id === this.position.id))
        .positions;
    }

    this.rowIndex = this.getRowIndex(this.position);
  }

  public actionListHandler({actionType, row, moveTo, returnData}): void {
    if (actionType === PositionsActionsType.INFO) {
      this.actionButtonsService.displaySidebarHandler(row.productId);
      return;
    }
    if (actionType === PositionsActionsType.TO_GROUP) {
      if (!returnData) {
        // create group and move position to it
        this.dialog.open(PositionGroupModalComponent, {
          data: {
            submitRequest$: (groupName: string) => {
              return this.positionGroupsService.createGroup(groupName, 'sbc',  this.data.currentSubscription.id)
                .pipe(tap((group: PositionGroupModel) => this.updatePosition('groupId', group.id)));
            }
          },
        });
        return;
      }
      // move to existed group
      this.updatePosition('groupId', returnData.groupId);
      return;
    }
    this.movePosition(row.id, moveTo);
  }

  private movePosition(posId: number, moveTo: number): void {
    this.subscriptionApi.changePositionOrder(posId, moveTo)
      .pipe(takeUntil(this.destroy$))
      .subscribe((data) => {
        this.position = data;
        this.getCurrentSubscription();
      });
  }

  public getCurrentSubscription(): void {
    this.subscriptionApi.getSubscriptionById(this.data.currentSubscription.id).subscribe();
  }

  public getRowIndex(position): number {
    let rowIndex: number;
    this.groupPositions.map((pos, index) => {
      if (pos.id === position.id) {
        rowIndex = index;
      }
    });
    return rowIndex;
  }

  private preparePosition(position: SalesPosition) {
      return {
        ...position,
        productName: `${position.description} ${!!position.productDescription ? '\n' + position.productDescription : ''}`,
        productNameServices: `${position.description} ${!!position.providedServices ? '\n' + position.providedServices : ''}`,
        providedWithin: this.getProvidedWithinValue(position),
        quantityWithUnitType: {
          value: position.quantity,
          unitType: this.unitTypeTranslatePipe.transform(position.unitType)
        },
        marginData: {
          profit: position.marginNet,
          percent: position.marginRate,
          currency: '€'
        },
      };
  }

  private getProvidedWithinValue(pos: SalesPosition): string {
    if (!pos.providedFrom) {
      return '-';
    }

    const providedFrom: string = this.datePipe.transform(pos.providedFrom, 'dd.MM.yy');
    const providedTo: string = pos.providedTo ? '-' + this.datePipe.transform(pos.providedTo, 'dd.MM.yy') : '';

    return `${providedFrom}${providedTo}`;
  }

  private getPositionsCards(): void {
    if (this.data.currentSubscription) {
      this.positionsCardInfoByTypes[this.position.productType] = generatePositionsCardsInfo(
        this.position.productType,
        this.data.currentSubscription.status,
        this.data.currentSubscription.preferences.priceOption,
        this.data.currentSubscription.properties.smallBusiness
      );
    }
  }

  public deletePositionDialog(): void {
    const dialog = this.dialog.open(DangerModalComponent, {
      data: REMOVE_POSITION_MODAL_DATA,
    });

    dialog.afterClosed().subscribe((res) => {
      if (res === CommonModalsActionsEnum.CONFIRM) {
        this.deletePositions([this.position.id]);
      }
    });
  }

  private deletePositions(ids: number[]): void {
    this.subscriptionApi.deleteSubscriptionPositions(this.data.currentSubscription.id, ids)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.getCurrentSubscription();
        this.dialogRef.close();
      });
  }

  public trackByProp(index: number, item: PositionInfoListModel) {
    return item.prop;
  }

  public updatePosition(cell: string, value: string|number): void {
    const field = {
      fieldName: cell,
      fieldValue: value
    };

    this.subscriptionApi.updateSubscriptionPosition(this.position.id, field, this.data.currentSubscription.id)
      // .pipe(takeUntil(this.destroy$))
      .subscribe((response) => {
        this.position = this.preparePosition(response.data);
        this.getCurrentSubscription();
      }, () => {
        if (cell === 'unitType') {
          this.redrawControls();
        }
      });
  }

  public redrawControls(): void {
    this.showControls = false;
    setTimeout(() => this.showControls = true);
  }

  public displayProductInfo(): void {
    this.actionButtonsService.displaySidebarHandler(this.position.productId);
  }

  public changePosition({position}): void {
    this.position = this.preparePosition(position);
    this.setupPositionsAndGroups();
    if (this.position.mediaType === PositionMediaTypeEnum.TEXT) {
      this.positionMediaFormComponentRef.resetTextEditorValue(this.position.description);
    }
    this.getPositionsCards();
    this.getCurrentRowIndex();
    this.getProductUnits();
    this.redrawControls();
  }

  public deleteMediaPositionFile(positionId: number): void {
    this.subscriptionApi.deleteMediaPositionFile(this.data.currentSubscription.id, positionId)
      .subscribe(() => {
        this.getCurrentSubscription();
        this.position = {
          ...this.position,
          fileUrl: null
        };
      });
  }

  public updateMediaPositionFile(positionId: number, event: { file: File; width: number; height: number }): void {
    this.subscriptionApi.updateMediaPositionFile(this.data.currentSubscription.id, positionId, event.file, event.width, event.height)
      .subscribe((groups: PositionGroupModel[]) => {
        this.getCurrentSubscription();
        const allPositions = [].concat(...groups.map(item => item.positions));
        this.position = { ...allPositions.find((p) => p.id === this.position.id) };
      }, () => {
        this.positionMediaFormComponentRef.resetImg();
        this.positionMediaFormComponentRef.resetImgInput();
      });
  }

  public getCurrentRowIndex(): void {
    this.rowIndex = this.groupPositions
      .findIndex(pos => pos.order === this.position.order && pos.positionId === this.position.positionId);
  }

  get allowFractionalValues(): boolean {
    if (!this.position.unitType || !this.unitsListSource.length) { return true; }
    const selectedUnit: ProductUnitModel = this.unitsListSource
      .find((itm: ProductUnitModel) => itm.name === this.position.unitType);
    const isFractionalQuantity: boolean = !!(this.position.quantity % 1);
    return (selectedUnit && !isFractionalQuantity)
      ? selectedUnit.allowFractionalValues
      : isFractionalQuantity;
  }

  get isGoods(): boolean {
    return this.position.productType === ProductTypes.GOODS && !this.position.general;
  }

  get priceOptionNet(): boolean {
    return this.data.currentSubscription.preferences.priceOption === PriceOptionEnum.NET
      || this.data.currentSubscription.properties.smallBusiness;
  }
  get priceOptionGross(): boolean {
    return this.data.currentSubscription.preferences.priceOption === PriceOptionEnum.GROSS
      && !this.data.currentSubscription.properties.smallBusiness;
  }

}
